"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { getToken, onMessage, type MessagePayload } from "firebase/messaging";

import { getMessagingInstance } from "@/libs/firebase";
import {
  DEVICE_TOKEN_STORAGE_KEY,
  registerDeviceToken,
} from "@/libs/api/deviceToken";
import {
  AUTH_CHANNEL_NAME,
  LOGOUT_MESSAGE,
  clearLocalPushState,
  isLoggingOut,
} from "@/libs/pushLogout";
import {
  clearAllNotifications,
  deleteNotification,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/libs/api/notifications";
import type {
  AdminNotificationEvent,
  Notification,
  NotificationPagination,
  NotificationType,
} from "@/types/notification";
import {
  NOTIFICATION_META,
  formatRelativeTime,
  getNotificationHref,
} from "@/components/header/notification/meta";

const PAGE_SIZE = 20;

/** A transient toast shown when a live notification arrives. */
interface Toast {
  id: string;
  notification: Notification;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  connected: boolean;
  /** Re-seed badge + first page (also used on socket reconnect). */
  refresh: () => Promise<void>;
  /** Fetch the next page for infinite scroll / "load more". */
  loadMore: () => Promise<void>;
  markRead: (id: Notification["id"]) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: Notification["id"]) => Promise<void>;
  clearAll: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

export const useNotifications = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return ctx;
};

/** Live socket items have no server id — mark them so we skip server calls. */
const isPersisted = (id: Notification["id"]): boolean =>
  typeof id === "number" ||
  (typeof id === "string" && !id.startsWith("live-"));

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: session, status } = useSession();
  const token = session?.accessToken;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState<NotificationPagination | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [connected, setConnected] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const liveIdRef = useRef(0);
  const toastIdRef = useRef(0);

  const hasMore = pagination?.hasNext ?? false;

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    (notification: Notification) => {
      toastIdRef.current += 1;
      const id = `toast-${toastIdRef.current}`;
      setToasts((prev) => [{ id, notification }, ...prev].slice(0, 4));
      // Auto-dismiss after 6s.
      setTimeout(() => dismissToast(id), 6000);
    },
    [dismissToast],
  );

  /** Re-seed the unread badge and the first page of the list. */
  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const {
        notifications: list,
        unreadCount: count,
        pagination: page,
      } = await getNotifications({ page: 1, limit: PAGE_SIZE });
      setNotifications(list);
      setPagination(page);
      // Prefer the dedicated count endpoint as the source of truth for the badge.
      try {
        setUnreadCount(await getUnreadCount());
      } catch {
        setUnreadCount(count);
      }
    } catch (err) {
      // 401s are handled globally by the axios interceptor (signs out).
      console.error("Failed to load notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadMore = useCallback(async () => {
    if (!token || loadingMore || !pagination?.hasNext) return;
    setLoadingMore(true);
    try {
      const next = pagination.page + 1;
      const { notifications: list, pagination: page } = await getNotifications({
        page: next,
        limit: PAGE_SIZE,
      });
      setNotifications((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...list.filter((n) => !seen.has(n.id))];
      });
      setPagination(page);
    } catch (err) {
      console.error("Failed to load more notifications:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [token, loadingMore, pagination]);

  const markRead = useCallback(
    async (id: Notification["id"]) => {
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === id);
        if (target && !target.isRead) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
        return prev.map((n) => (n.id === id ? { ...n, isRead: true } : n));
      });
      if (isPersisted(id)) {
        try {
          await markNotificationRead(id);
        } catch (err) {
          console.error("Failed to mark notification read:", err);
        }
      }
    },
    [],
  );

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch (err) {
      console.error("Failed to mark all read:", err);
    }
  }, []);

  const remove = useCallback(async (id: Notification["id"]) => {
    setNotifications((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.isRead) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      return prev.filter((n) => n.id !== id);
    });
    setPagination((p) => (p ? { ...p, total: Math.max(0, p.total - 1) } : p));
    if (isPersisted(id)) {
      try {
        await deleteNotification(id);
      } catch (err) {
        console.error("Failed to delete notification:", err);
      }
    }
  }, []);

  const clearAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    setPagination((p) =>
      p ? { ...p, total: 0, totalPages: 0, hasNext: false } : p,
    );
    try {
      await clearAllNotifications();
    } catch (err) {
      console.error("Failed to clear notifications:", err);
    }
  }, []);

  /** Handle a live FCM foreground push. */
  const handleIncoming = useCallback(
    (event: AdminNotificationEvent) => {
      liveIdRef.current += 1;
      const notification: Notification = {
        id: `live-${liveIdRef.current}`,
        userId: event.data?.userId ?? "",
        title: event.title,
        body: event.body,
        type: event.type,
        isSent: true,
        sentAt: event.createdAt,
        isRead: false,
        createdAt: event.createdAt,
        data: event.data,
      };
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((c) => c + 1);
      setPagination((p) => (p ? { ...p, total: p.total + 1 } : p));
      pushToast(notification);
    },
    [pushToast],
  );

  // ── FCM push lifecycle ────────────────────────────────────────────────────
  // Register this browser's push token once the admin is authenticated. This
  // dashboard is admin-only, so an authenticated session implies an admin.
  useEffect(() => {
    if (status !== "authenticated" || !token) return;

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      try {
        if (!("serviceWorker" in navigator) || !("Notification" in window)) {
          return;
        }

        const messaging = await getMessagingInstance();
        if (!messaging || cancelled) return;

        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );

        const permission = await Notification.requestPermission();
        if (permission !== "granted" || cancelled) {
          setConnected(false);
          return;
        }

        // Re-run on every authenticated app load — getToken() returns the
        // existing valid token, or mints a fresh one if it rotated, so this
        // also covers FCM's periodic token refresh.
        const fcmToken = await getToken(messaging, {
          vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (!fcmToken || cancelled || isLoggingOut()) return;

        localStorage.setItem(DEVICE_TOKEN_STORAGE_KEY, fcmToken);
        await registerDeviceToken(fcmToken);
        if (cancelled) return;
        setConnected(true);

        // Foreground push — the tab is focused, so show an in-app toast
        // instead of relying on the service worker's OS notification.
        unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
          const data = payload.data ?? {};
          handleIncoming({
            title: payload.notification?.title ?? data.title ?? "New notification",
            body: payload.notification?.body ?? data.body ?? "",
            type: (data.type as NotificationType) ?? "GENERAL",
            data,
            createdAt: new Date().toISOString(),
          });
        });

        // Background push — the service worker handled it (tab unfocused,
        // minimized, or in another tab) and posts it here so the badge/list
        // still update live instead of waiting for the next manual refresh.
        const onSwMessage = (event: MessageEvent) => {
          if (event.data?.type !== "FCM_PUSH") return;
          const payload = event.data.payload as MessagePayload;
          const data = payload.data ?? {};
          handleIncoming({
            title: payload.notification?.title ?? data.title ?? "New notification",
            body: payload.notification?.body ?? data.body ?? "",
            type: (data.type as NotificationType) ?? "GENERAL",
            data,
            createdAt: new Date().toISOString(),
          });
        };
        navigator.serviceWorker.addEventListener("message", onSwMessage);
        const removeSwListener = unsubscribe;
        unsubscribe = () => {
          removeSwListener?.();
          navigator.serviceWorker.removeEventListener("message", onSwMessage);
        };
      } catch (err) {
        setConnected(false);
        console.error("Failed to set up push notifications:", err);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
      setConnected(false);
      setNotifications([]);
      setUnreadCount(0);
      setPagination(null);
    };
  }, [status, token, handleIncoming]);

  // Another tab signed out — run the same cleanup here. The originating tab
  // already revoked the (browser-wide) push token, so only local state is
  // cleared before ending this tab's session.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.onmessage = async (event: MessageEvent) => {
      if (event.data !== LOGOUT_MESSAGE) return;
      await clearLocalPushState();
      await signOut({ redirect: false });
      window.location.href = "/signin";
    };
    return () => channel.close();
  }, []);

  // Seed the badge + list once authenticated (deferred so the fetch's loading
  // state doesn't set state synchronously during the effect's commit).
  useEffect(() => {
    if (status !== "authenticated" || !token) return;
    const id = setTimeout(() => void refresh(), 0);
    return () => clearTimeout(id);
  }, [status, token, refresh]);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    connected,
    refresh,
    loadMore,
    markRead,
    markAllRead,
    remove,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </NotificationContext.Provider>
  );
};

// ── Toast viewport ──────────────────────────────────────────────────────────

const ToastViewport: React.FC<{
  toasts: Toast[];
  onDismiss: (id: string) => void;
}> = ({ toasts, onDismiss }) => {
  const router = useRouter();
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-5 right-5 z-999999 flex w-full max-w-sm flex-col gap-3">
      {toasts.map(({ id, notification }) => {
        const meta = NOTIFICATION_META[notification.type] ?? NOTIFICATION_META.GENERAL;
        const Icon = meta.icon;
        const href = getNotificationHref(notification.type, notification.data);

        return (
          <div
            key={id}
            role="button"
            tabIndex={0}
            onClick={() => {
              if (href) router.push(href);
              onDismiss(id);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (href) router.push(href);
                onDismiss(id);
              }
            }}
            className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-theme-lg transition hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-dark dark:hover:bg-white/5"
          >
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${meta.iconWrap}`}
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-800 dark:text-white/90">
                {notification.title}
              </p>
              <p className="mt-0.5 line-clamp-2 text-theme-sm text-gray-500 dark:text-gray-400">
                {notification.body}
              </p>
              <span className="mt-1 block text-theme-xs text-gray-400">
                {formatRelativeTime(notification.createdAt)}
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(id);
              }}
              aria-label="Dismiss notification"
              className="text-gray-400 transition hover:text-gray-600 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
};
